import React, { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { GRID_SIZE } from '../Map/Grid';
import { useSelector } from 'react-redux';
import { Rootstate } from '../../store';
import patterns, { Patterns } from './spellPatterns';
import SpellIcon from '../Icons/SpellIcon';
import { EditorHotbarContext } from '../Map/EditorHotbar';

const SpellEffectsPopup = () => {
    const [size, setSize] = useState(20);

    const [pattern, setPattern] = useState<keyof Patterns>('cone');

    const [style, setStyle] = useState<CSSProperties>({
        transformOrigin: 'top left',
        display: 'none',
    });

    const zoom = useSelector((state: Rootstate) => state.zoom.zoom);

    const zoomRef = useRef(zoom);

    useEffect(() => {
        zoomRef.current = zoom;
    }, [zoom]);
    const rotationPointRef = useRef<{ x: number, y: number }>();
    const [rotationPoint, setRotationPoint] = useState<{ x: number, y: number }>();
    useEffect(() => {
        const holder = document.getElementById('mapeditor-holder') as HTMLDivElement;
        const onMouseMove = (e: MouseEvent) => {
            if (rotationPointRef.current) {
                const dx = e.pageX - rotationPointRef.current.x;
                const dy = e.pageY - rotationPointRef.current.y;
                const angle = Math.atan2(dy, dx) * (180 / Math.PI); // Convert radians to degrees
                const distance = Math.sqrt(dx * dx + dy * dy); // Calculate distance
                setSize(distance / (zoomRef.current * 5)); // Update size based on distance
                setStyle((oldStyle) => ({
                    ...oldStyle,
                    transform: `rotate(${angle}deg)`,
                }));
            }
        };


        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            setRotationPoint({ x: e.pageX, y: e.pageY });
            rotationPointRef.current = { x: e.pageX, y: e.pageY };
            const onMouseUp = () => {
                setSize(5);
                setStyle((oldStyle) => ({
                    ...oldStyle,
                    display: 'none',
                }));
                setRotationPoint(undefined);
                holder.removeEventListener('mousemove', onMouseMove);
                holder.removeEventListener('mouseup', onMouseUp);
            };
            setStyle((oldStyle) => ({
                ...oldStyle,
                top: e.pageY,
                left: e.pageX,
                display: 'block',
            }));
            holder.addEventListener('mousemove', onMouseMove);
            holder.addEventListener('mouseup', onMouseUp);
        };

        holder.addEventListener('mousedown', onMouseDown);
        return () => {
            holder.removeEventListener('mousedown', onMouseDown);
            holder.removeEventListener('mousemove', onMouseMove);
        };
    }, []);
    const actualTransform =
        `scale(${zoom}) ${style?.transform ?? ''}`;

    return (
        <>
            {!Number.isNaN(size) && (
                <>
                    <div
                        style={{
                            ...style,
                            transform: actualTransform,
                        }}
                        className='fixed pointer-events-none'
                    >
                        <svg
                            height={`${(GRID_SIZE * size / 5)}`}
                            width={`${(GRID_SIZE * size / 5)}`}
                            className='overflow-visible'>
                            {patterns[pattern](size)}
                        </svg>
                    </div>
                    {rotationPoint && (
                        <div
                            className={'fixed bg-slate-100 rounded-xl p-1'
                                + ' text-slate-950 text-xs z-20 pointer-events-none'}
                            style={{
                                left: `${(rotationPoint?.x ?? 0)}px`,
                                top: `${(rotationPoint?.y ?? 0)}px`,
                            }}
                        >
                            {Math.round(size * 100) / 100} feet
                        </div>
                    )}
                </>
            )}
            <div className={`rounded-bl-xl p-3 bg-mid-blue`}>
                <div className="rounded-xl p-3 bg-deep-blue flex flex-col gap-3">
                    <div>
                        <div className='text-xs mb-1 font-medium'>
                            Spell Template
                        </div>
                        <select
                            id="shapes"
                            className={"bg-gray-50 border border-gray-300= text-sm"
                                + " rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
                                + " bg-gray-700 border-gray-600 placeholder-gray-400 text-white"
                                + " focus:ring-blue-500 focus:border-blue-500"}
                            onChange={(e) => setPattern(e.target.value as keyof Patterns)}
                        >
                            <option value="cone">Cone</option>
                            <option value="sphere">Sphere</option>
                            <option value="cube">Cube</option>
                            <option value="line">Line</option>
                        </select>
                    </div>
                </div>
            </div>
        </>
    );
};

const SpellEffectEditor = () => {
    const { setActiveElement, id } = useContext(EditorHotbarContext);

    const handleClick = () => {
        const popup = (
            <SpellEffectsPopup />
        );
        setActiveElement('spell-selector', (
            <>
                {popup}
            </>
        ));
    };

    return (
        <>
            <div
                className={
                    'flex items-center cursor-pointer justify-center h-16 min-w-18'
                    + ` ${id === 'spell-selector' ? 'bg-mid-blue' : ''}`}
                onClick={handleClick}
            >
                <div className="pl-2 pr-2 ">
                    <SpellIcon className="h-7" />
                </div>
            </div>
        </>
    );
};

export default SpellEffectEditor;
